/**
 * BrainMonitorPage — v3 · Scalable table view for 100+ tenants
 *
 * ✅ Compact table row per tenant (scales to 100+)
 * ✅ Live search by name / slug
 * ✅ Status filter chips (All / Running / Stale / Stopped)
 * ✅ Logs open in a NEW MDI tab (not dialog)
 * ✅ Expandable AI Employees per row
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMdiStore } from '@/stores/mdi-store';
import {
  RefreshCw, Loader2, Activity, ServerCrash, CheckCircle2, AlertTriangle,
  Clock, Power, PowerOff, FileText, ChevronDown, ChevronUp,
  Users, Search, Zap, Cpu, Brain,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TenantHealth {
  tenantId: string;
  name: string;
  slug: string;
  brain_enabled: boolean;
  status: string;
  activeGoals: number;
  processedGoals: number;
  uptime: number;
  lastPollAt: string | null;
  pid: number | null;
  host: string | null;
  lastHeartbeatAt: string | null;
  stale: boolean;
  aiEmployeeCount: number;
  aiEmployeeActiveCount: number;
}

interface MonitorData {
  totalTenants: number;
  activeTenants: number;
  staleTenants: number;
  tenants: TenantHealth[];
  timestamp: string;
}

interface AiEmployee {
  id: string;
  employee_code: string;
  name: string;
  display_name?: string; // alias
  role: string;
  department: string;
  status: string;
  skills: string[];
}

type StatusFilter = 'all' | 'running' | 'stale' | 'stopped' | 'never';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatUptime(ms: number): string {
  if (ms <= 0) return '—';
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${sec}s`;
}

function ageFromNow(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.floor(min / 60)}h ago`;
}

function initials(name: string): string {
  return name.split(/[\s\-_]+/).slice(0, 2).map((w: string) => w[0]?.toUpperCase() ?? '').join('');
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, stale }: { status: string; stale: boolean }) {
  if (stale) return (
    <Badge variant="destructive" className="gap-1 text-xs">
      <AlertTriangle className="h-3 w-3" />stale
    </Badge>
  );
  if (status === 'running') return (
    <Badge className="gap-1 text-xs bg-emerald-600 hover:bg-emerald-700">
      <CheckCircle2 className="h-3 w-3" />running
    </Badge>
  );
  if (status === 'stopped') return (
    <Badge variant="secondary" className="gap-1 text-xs">
      <ServerCrash className="h-3 w-3" />stopped
    </Badge>
  );
  if (status === 'never') return (
    <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
      <Clock className="h-3 w-3" />never started
    </Badge>
  );
  return <Badge variant="outline" className="text-xs">{status}</Badge>;
}

// ─── AI Employees expanded row ────────────────────────────────────────────────

function EmployeesRow({ tenantId }: { tenantId: string }) {
  const [employees, setEmployees] = useState<AiEmployee[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<AiEmployee[]>(`/monitoring/tenants/${tenantId}/employees`);
      setEmployees(Array.isArray(res) ? res : []);
    } catch { setEmployees([]); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return (
    <div className="flex items-center gap-2 py-3 px-8 text-xs text-muted-foreground">
      <Loader2 className="h-3 w-3 animate-spin" />Loading employees…
    </div>
  );

  if (!employees?.length) return (
    <div className="py-3 px-8 text-xs text-muted-foreground italic">No AI employees deployed.</div>
  );

  const grouped = new Map<string, AiEmployee[]>();
  for (const e of employees) {
    const dept = e.department || 'General';
    if (!grouped.has(dept)) grouped.set(dept, []);
    grouped.get(dept)!.push(e);
  }

  return (
    <div className="px-8 py-3 space-y-2.5 bg-muted/20 border-t border-border/30">
      {Array.from(grouped.entries()).map(([dept, emps]) => (
        <div key={dept}>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
            {dept}
          </div>
          <div className="flex flex-wrap gap-2">
            {emps.map(e => (
              <div key={e.id}
                className="flex items-center gap-2 rounded-lg border bg-background px-2.5 py-1.5 text-xs">
                <div
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
                  style={{ background: 'var(--category-indicator)' }}
                >
                  {initials(e.display_name ?? '')}
                </div>
                <div>
                  <div className="font-medium leading-tight">{e.display_name}</div>
                  <div className="text-muted-foreground text-[10px]">{e.role}</div>
                </div>
                <span className={`ml-1 h-1.5 w-1.5 rounded-full ${
                  e.status === 'active' ? 'bg-emerald-500' :
                  e.status === 'paused' ? 'bg-amber-400' : 'bg-slate-400'
                }`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tenant Table Row ─────────────────────────────────────────────────────────

function TenantRow({
  t,
  onToggle,
  toggling,
  onOpenLogs,
  onOpenSoul,
}: {
  t: TenantHealth;
  onToggle: (tid: string, enable: boolean) => void;
  toggling: boolean;
  onOpenLogs: (t: TenantHealth) => void;
  onOpenSoul: (t: TenantHealth) => void;
}) {
  const [empExpanded, setEmpExpanded] = useState(false);

  const rowCls = t.stale
    ? 'bg-amber-50/40 dark:bg-amber-950/10 hover:bg-amber-50/70'
    : t.status !== 'running'
      ? 'opacity-60 hover:opacity-100 hover:bg-muted/30'
      : 'hover:bg-muted/30';

  return (
    <>
      <tr className={`border-b border-border/40 transition-all ${rowCls}`}>
        {/* Identity */}
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white shadow-sm"
              style={{ background: 'var(--category-indicator)' }}
            >
              {initials(t.name)}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm leading-tight">{t.name}</div>
              <div className="text-[11px] text-muted-foreground font-mono">{t.slug}</div>
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="px-3 py-2.5 whitespace-nowrap">
          <StatusBadge status={t.status} stale={t.stale} />
        </td>

        {/* Active Goals */}
        <td className="px-3 py-2.5 text-sm tabular-nums text-center">
          <span className="inline-flex items-center gap-1 justify-center">
            <Zap className="h-3 w-3 text-muted-foreground" />
            <span className={t.activeGoals > 0 ? 'font-semibold' : 'text-muted-foreground'}>
              {t.activeGoals}
            </span>
          </span>
        </td>

        {/* Processed */}
        <td className="px-3 py-2.5 text-sm tabular-nums text-right text-muted-foreground">
          {t.processedGoals.toLocaleString()}
        </td>

        {/* Uptime */}
        <td className="px-3 py-2.5 text-sm tabular-nums text-right text-muted-foreground">
          {formatUptime(t.uptime)}
        </td>

        {/* Last beat */}
        <td className={`px-3 py-2.5 text-sm tabular-nums text-right ${
          t.stale ? 'text-amber-500 font-medium' : 'text-muted-foreground'
        }`}>
          {ageFromNow(t.lastHeartbeatAt)}
        </td>

        {/* PID */}
        <td className="px-3 py-2.5 text-xs font-mono text-right text-muted-foreground">
          <span className="inline-flex items-center justify-end gap-1">
            <Cpu className="h-3 w-3" />{t.pid ?? '—'}
          </span>
        </td>

        {/* Actions */}
        <td className="px-3 py-2.5 text-right">
          <div className="inline-flex items-center gap-0.5">
            {t.brain_enabled ? (
              <Button
                size="sm" variant="ghost"
                className="h-7 gap-1 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 px-2"
                disabled={toggling}
                onClick={() => onToggle(t.tenantId, false)}
              >
                {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <PowerOff className="h-3 w-3" />}
                Disable
              </Button>
            ) : (
              <Button
                size="sm" variant="ghost"
                className="h-7 gap-1 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2"
                disabled={toggling}
                onClick={() => onToggle(t.tenantId, true)}
              >
                {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
                Enable
              </Button>
            )}

            {/* Logs → opens new tab */}
            <Button
              size="sm" variant="ghost"
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-primary px-2"
              onClick={() => onOpenSoul(t)}
            >
              <Brain className="h-3 w-3" />Soul ↗
            </Button>

            <Button
              size="sm" variant="ghost"
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-primary px-2"
              onClick={() => onOpenLogs(t)}
            >
              <FileText className="h-3 w-3" />Logs ↗
            </Button>

            {/* Expand employees */}
            <Button
              size="sm" variant="ghost"
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground px-2"
              onClick={() => setEmpExpanded(v => !v)}
            >
              <Users className="h-3 w-3" />
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0" title={`${t.aiEmployeeActiveCount} active / ${t.aiEmployeeCount} total`}>
                {t.aiEmployeeActiveCount}/{t.aiEmployeeCount}
              </Badge>
              {empExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          </div>
        </td>
      </tr>

      {/* Employees expansion row */}
      {empExpanded && (
        <tr className="border-b border-border/40">
          <td colSpan={8} className="p-0">
            <EmployeesRow tenantId={t.tenantId} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BrainMonitorPage() {
  const [data, setData] = useState<MonitorData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [togglingTid, setTogglingTid] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const navigate = useNavigate();
  const openTab = useMdiStore(s => s.openTab);

  // ── Load ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<MonitorData>('/monitoring/tenants');
      setData(res);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  // ── Toggle brain ─────────────────────────────────────────────────────────
  const toggleBrain = useCallback(async (tid: string, enable: boolean) => {
    setTogglingTid(tid);
    try {
      await api.post(`/platform/tenants/${tid}/brain/${enable ? 'enable' : 'disable'}`, {});
      setData(prev => prev ? {
        ...prev,
        tenants: prev.tenants.map(t => t.tenantId === tid ? { ...t, brain_enabled: enable } : t),
      } : prev);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Toggle failed');
    } finally {
      setTogglingTid(null);
    }
  }, []);

  // ── Open logs in new MDI tab ─────────────────────────────────────────────
  const openLogs = useCallback((t: TenantHealth) => {
    const path = `/admin/brain-logs?tenantId=${encodeURIComponent(t.tenantId)}&tenantName=${encodeURIComponent(t.name)}`;
    openTab({ title: `Logs · ${t.name}`, path, icon: 'file-text' });
    navigate(path);
  }, [openTab, navigate]);

  const openSoul = useCallback((t: TenantHealth) => {
    const path = `/admin/brain-soul?tenantId=${encodeURIComponent(t.tenantId)}&tenantName=${encodeURIComponent(t.name)}`;
    openTab({ title: `Soul · ${t.name}`, path, icon: 'brain' });
    navigate(path);
  }, [openTab, navigate]);

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!data) return [];
    return data.tenants.filter(t => {
      if (statusFilter === 'running' && (t.stale || t.status !== 'running')) return false;
      if (statusFilter === 'stale'   && !t.stale) return false;
      if (statusFilter === 'stopped' && (t.stale || t.status !== 'stopped')) return false;
      if (statusFilter === 'never'   && t.status !== 'never') return false;
      if (search) {
        const q = search.toLowerCase();
        return t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q);
      }
      return true;
    });
  }, [data, search, statusFilter]);

  const counts = useMemo(() => ({
    all:     data?.tenants.length ?? 0,
    running: data?.activeTenants ?? 0,
    stale:   data?.staleTenants ?? 0,
    stopped: data?.tenants.filter(t => !t.stale && t.status === 'stopped').length ?? 0,
    never:   data?.tenants.filter(t => t.status === 'never').length ?? 0,
  }), [data]);

  const PILL_ACTIVE: Record<StatusFilter, string> = {
    all:     'bg-foreground text-background border-foreground',
    running: 'bg-emerald-600 text-white border-emerald-600',
    stale:   'bg-amber-500 text-white border-amber-500',
    stopped: 'bg-slate-500 text-white border-slate-500',
    never:   'bg-muted text-muted-foreground border-muted-foreground',
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-5 py-3 border-b bg-background/95 backdrop-blur flex-shrink-0">
        <Activity className="h-4 w-4 shrink-0" style={{ color: 'var(--category-indicator)' }} />
        <div className="flex flex-col leading-tight mr-1">
          <span className="font-bold text-sm">Brain Monitor</span>
          <span className="text-[10px] text-muted-foreground">AI daemon status · auto-refresh every 30s</span>
        </div>

        {/* Status filter pills */}
        <div className="flex items-center gap-1.5">
          {(['all', 'running', 'stale', 'stopped', 'never'] as StatusFilter[]).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border transition-colors ${
                statusFilter === s
                  ? PILL_ACTIVE[s]
                  : 'bg-transparent text-muted-foreground border-border hover:border-muted-foreground'
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}{' '}
              <span className="opacity-70">{counts[s]}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8 h-8 text-sm bg-muted/40"
            placeholder="Search name or slug…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch('')}
            >
              <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2.5">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />{ageFromNow(lastRefresh.toISOString())}
            </span>
          )}
          <span className="text-xs text-muted-foreground tabular-nums">{filtered.length} tenants</span>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading} className="h-8">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-5 py-2.5 text-sm text-destructive bg-destructive/10 border-b border-destructive/20 flex-shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm border-collapse min-w-[900px]">
          <thead className="sticky top-0 z-10">
            <tr className="border-b bg-muted/60 backdrop-blur text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2.5 text-left">Tenant</th>
              <th className="px-3 py-2.5 text-left w-[110px]">Status</th>
              <th className="px-3 py-2.5 text-center w-[80px]">Active</th>
              <th className="px-3 py-2.5 text-right w-[100px]">Processed</th>
              <th className="px-3 py-2.5 text-right w-[80px]">Uptime</th>
              <th className="px-3 py-2.5 text-right w-[90px]">Last beat</th>
              <th className="px-3 py-2.5 text-right w-[70px]">PID</th>
              <th className="px-3 py-2.5 text-right w-[280px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr>
                <td colSpan={8} className="py-24 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground inline-block" />
                </td>
              </tr>
            )}

            {!loading && !filtered.length && (
              <tr>
                <td colSpan={8} className="py-24 text-center text-muted-foreground text-sm">
                  {search || statusFilter !== 'all'
                    ? 'No tenants match the current filter'
                    : 'No brain heartbeats found. Start agent-brain to see status here.'}
                </td>
              </tr>
            )}

            {filtered.map(t => (
              <TenantRow
                key={t.tenantId}
                t={t}
                onToggle={toggleBrain}
                toggling={togglingTid === t.tenantId}
                onOpenLogs={openLogs}
                onOpenSoul={openSoul}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

